import AuthLayout from "./layout";
import { useNavigate } from "react-router-dom";
import {
  LOGIN_COMPLETE_PAGE_ROUTE,
  LOGIN_PAGE_ROUTE,
} from "@pma/utils/page-routes";
import { useEffect, useState } from "react";
import axios from "axios";
import { ACCEPT_INVITE_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes";
import { useMutation } from "@tanstack/react-query";
import PmsLoader from "@components/common/loader";
import { toast } from "@hooks/use-toast";
import useTranslator from "@hooks/use-translator";
import { DictType } from "@pma/types";
import Recaptcha from "@components/auth/recaptcha";
import { useAuth } from "@stores/auth-store";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";

const InvitationsAcceptPage = () => {
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({
    v2: "",
    v3: "",
  });

  const [v2Fallback, setV2Fallback] = useState(false);
  const navigate = useNavigate();
  const { translate } = useTranslator();

  const { authConfig, signIn } = useAuth();

  const invitationKey = authConfig?.invitationKey;
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const acceptInvite = async (payload: DictType) => {
    if (!v2Fallback && !recaptchaToken.v3) {
      return toast({
        title: translate("Info"),
        description: translate("Please complete the reCAPTCHA"),
        variant: "info",
      });
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      return;
    }

    const response = await axios.post(
      SERVER_URL + ACCEPT_INVITE_ENDPOINT,

      {
        key: invitationKey,
        recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
        recaptcha_response_v2: payload?.v2Token,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const acceptInvitationMutation = useMutation({
    mutationKey: ["accept-invitation"],
    mutationFn: (payload: DictType) => acceptInvite(payload),
    onSuccess: (data) => {
      // Reset state invitationKey
      signIn({ invitationKey: undefined });
      navigate(LOGIN_COMPLETE_PAGE_ROUTE);
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      if (errorMessage === "Invalid Captcha Response. Please try again.") {
        setV2Fallback(true)
      } else {
        toast({
          title: translate("Error"),
          description: errorMessage || translate("Unable to accept invite at this time"),
          variant: "error",
        })

        // Reset state invitationKey

        signIn({ invitationKey: undefined })

        navigate(LOGIN_PAGE_ROUTE)
      }
    },
  })

  useEffect(() => {
    if (invitationKey) {
      // Delay for recaptcha request to fullfil
      setTimeout(() => {
        acceptInvitationMutation.mutate({})
      }, 2000)
    } else {
      toast({
        title: translate("Error"),
        description: translate("Unable to accept invite at this time"),
        variant: "error",
      })
      navigate(LOGIN_PAGE_ROUTE);
    }
  }, [])

  const reSubmit = (token: string) => {
    acceptInvitationMutation.mutate({ v2Token: token });
  };

  return (
    <AuthLayout>
      <PmsLoader />
      <p className="animate-pulse text-base text-muted">
        {translate("A moment please")}...
      </p>
      {/* GOOGLE Recaptcha */}
      <Recaptcha
        setToken={setRecaptchaToken}
        v2={v2Fallback}
        reSubmit={reSubmit}
      />
    </AuthLayout>
  );
};

export default InvitationsAcceptPage;
